// KBC-style answer panels: 2x2 grid of long horizontal LOZENGE-shaped buttons with chevron
// points on both ends (the iconic KBC look). Each panel has a hexagonal letter chip on the
// left side and the option text in the middle.
//
// States per option:
//   idle    — deep navy gradient with amber chevron border (classic look)
//   selected (pending lock) — animated orange-amber gradient
//   locked-correct — green flash + outer glow
//   locked-wrong   — red flash + horizontal shake
//   eliminated (50:50) — fades to ~15% opacity
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { OPTION_LETTERS } from './kbcEngine';

export default function AnswerPanels({
    options,
    selectedIndex,
    lockedIndex,
    correctIndex,
    eliminatedIndices,
    disabled,
    onSelect,
}) {
    const reveal = lockedIndex !== null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="kbc-answers">
            {options.map((text, i) => {
                const isSelected = selectedIndex === i;
                const isLocked = lockedIndex === i;
                const isEliminated = eliminatedIndices?.includes(i);
                const showAsRight = reveal && i === correctIndex;
                const showAsWrong = reveal && isLocked && i !== correctIndex;
                return (
                    <LozengePanel
                        key={i}
                        letter={OPTION_LETTERS[i]}
                        text={text}
                        isSelected={isSelected && !reveal}
                        showAsRight={showAsRight}
                        showAsWrong={showAsWrong}
                        isEliminated={isEliminated && !reveal}
                        onClick={!disabled && !isEliminated && !reveal ? () => onSelect(i) : undefined}
                        index={i}
                    />
                );
            })}
        </div>
    );
}

// The lozenge / hexagonal-pill shape — classic KBC. Two SVG layers stacked: outer chevron
// border + inner fill. Letter chip on the left.
function LozengePanel({ letter, text, isSelected, showAsRight, showAsWrong, isEliminated, onClick, index }) {
    // Color palette per state
    let fill = { from: '#0c1f5a', via: '#1e3a8a', to: '#0a103a' };  // idle navy
    let border = '#fbbf24';                                          // idle amber
    let glow = '0 4px 14px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)';
    let textColor = '#ffffff';
    let chipFill = { from: '#fbbf24', to: '#b45309' };
    let pulse = {};

    if (isSelected) {
        fill = { from: '#f97316', via: '#ea580c', to: '#9a3412' };
        border = '#fde68a';
        glow = '0 0 36px rgba(249,115,22,0.85), 0 8px 24px rgba(0,0,0,0.55), inset 0 2px 0 rgba(255,255,255,0.22)';
        chipFill = { from: '#fef3c7', to: '#fbbf24' };
        pulse = { scale: [1, 1.025, 1] };
    }
    if (showAsRight) {
        fill = { from: '#059669', via: '#047857', to: '#064e3b' };
        border = '#6ee7b7';
        glow = '0 0 44px rgba(16,185,129,0.95), 0 8px 24px rgba(0,0,0,0.55), inset 0 2px 0 rgba(255,255,255,0.25)';
        chipFill = { from: '#d1fae5', to: '#34d399' };
        pulse = { scale: [1, 1.06, 1] };
    }
    if (showAsWrong) {
        fill = { from: '#dc2626', via: '#b91c1c', to: '#7f1d1d' };
        border = '#fca5a5';
        glow = '0 0 36px rgba(239,68,68,0.9), 0 8px 24px rgba(0,0,0,0.55)';
        chipFill = { from: '#fee2e2', to: '#fca5a5' };
        pulse = { x: [-10, 10, -8, 8, -4, 4, 0] };
    }
    if (isEliminated) {
        textColor = 'transparent';
    }

    return (
        <motion.button
            type="button"
            onClick={onClick}
            disabled={!onClick}
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: isEliminated ? 0.15 : 1, scaleX: 1, ...pulse }}
            transition={
                Object.keys(pulse).length
                    ? (showAsWrong
                        ? { duration: 0.6 }
                        : (isSelected
                            ? { repeat: Infinity, duration: 1.05, ease: 'easeInOut' }
                            : { duration: 0.45 }))
                    : { delay: 0.2 + index * 0.18, duration: 0.55, ease: [0.22, 1.4, 0.36, 1] }
            }
            whileHover={onClick ? { scale: 1.025 } : {}}
            whileTap={onClick ? { scale: 0.97 } : {}}
            className="relative w-full text-left disabled:cursor-not-allowed transition-shadow"
            style={{ minHeight: '60px', filter: showAsRight ? 'drop-shadow(0 0 18px rgba(16,185,129,0.5))' : undefined }}
            data-testid={`kbc-option-${letter}`}
        >
            {/* SVG hexagonal lozenge — outer border + inner gradient fill. Aspect-flexible. */}
            <svg viewBox="0 0 600 80" preserveAspectRatio="none" className="w-full h-[60px] sm:h-[68px]" style={{ overflow: 'visible' }}>
                <defs>
                    <linearGradient id={`fill-${index}-${letter}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={fill.from} />
                        <stop offset="55%" stopColor={fill.via} />
                        <stop offset="100%" stopColor={fill.to} />
                    </linearGradient>
                    <linearGradient id={`gloss-${index}-${letter}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>
                    <linearGradient id={`chip-${index}-${letter}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={chipFill.from} />
                        <stop offset="100%" stopColor={chipFill.to} />
                    </linearGradient>
                </defs>
                {/* Lozenge body — chevron points on left & right */}
                <polygon
                    points="40,4 560,4 596,40 560,76 40,76 4,40"
                    fill={`url(#fill-${index}-${letter})`}
                    stroke={border}
                    strokeWidth="2.5"
                    style={{ filter: `drop-shadow(${glow})` }}
                />
                {/* Top gloss strip */}
                <polygon
                    points="50,6 555,6 575,28 525,28 50,28"
                    fill={`url(#gloss-${index}-${letter})`}
                    opacity="0.55"
                />
                {/* Letter chip (hex) on the left */}
                <polygon
                    points="62,16 96,16 113,40 96,64 62,64 45,40"
                    fill={`url(#chip-${index}-${letter})`}
                    stroke="rgba(0,0,0,0.5)"
                    strokeWidth="1.5"
                />
                <text x="79" y="48" textAnchor="middle" fontWeight="900" fontSize="20"
                      fill="#0f172a" style={{ fontFamily: 'inherit' }}>
                    {letter}:
                </text>
            </svg>
            {/* Option text overlayed */}
            <div className="absolute inset-0 flex items-center pl-[20%] pr-[10%]">
                <AnimatePresence>
                    {!isEliminated && (
                        <motion.span
                            key={`txt-${letter}`}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ delay: 0.35 + index * 0.18, duration: 0.4 }}
                            className="font-display font-bold text-sm sm:text-base leading-snug"
                            style={{ color: textColor, textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}
                        >
                            {text}
                        </motion.span>
                    )}
                </AnimatePresence>
            </div>
        </motion.button>
    );
}
